import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import Contact from '@components/cart/models/Contact';
import States from '@components/cart/models/States';
import { RX_CELL_TYPES } from '@utils/enums';
import { changeCart, getShopifyCart } from '@utils/shopify-helpers';
import { useDispatch } from 'react-redux';
import { setCartInfo } from '@features/cart/cartSlice';
import { isEmpty } from 'lodash';

const DoctorModal = ({ isDisplayed, onSubmit, onCancel, bundleKey, item }) => {
	const dispatch = useDispatch();
	const [fields, setFields] = useState({
		patient_full_name: '',
		patient_birthdate: '',
		clinic_name: '',
		clinic_state: '',
		clinic_phone: '',
	});
	const [errors, setErrors] = useState({});

	const handleSubmit = () => {
		let contact = new Contact();
		const { state, errors: newErrors } = contact.validate(fields);

		if (state) {
			let prescription = {
				contact: {
					full_name: fields.patient_full_name,
					birthday: fields.patient_birthdate,
					clinic: fields.clinic_name,
					state: fields.clinic_state,
					phone: fields.clinic_phone,
					key: bundleKey,
				},
			};

			// Submit Doctor Contact Form to Pair API
			contact
				.set(prescription.contact)
				.submit()
				.then(async response => {
					const updatedData = {
						id: item.key,
						properties: {
							...item.properties,
							_submissionMethod: RX_CELL_TYPES.DOCTOR,
						},
					};

					await changeCart(updatedData);
					const newCartData = await getShopifyCart('/');
					dispatch(setCartInfo(newCartData));
					setFields({
						patient_full_name: '',
						patient_birthdate: '',
						clinic_name: '',
						clinic_state: '',
						clinic_phone: '',
					});
					setErrors({});
					onSubmit();
				});
		} else {
			setErrors({ ...newErrors, ...errors });
		}
	};

	const isValidDate = dob => {
		if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dob)) return false;

		const parts = dob.split('/');
		const day = parseInt(parts[1], 10);
		const month = parseInt(parts[0], 10);
		const year = parseInt(parts[2], 10);

		if (year < 1000 || year > 3000 || month === 0 || month > 12) return false;

		const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

		if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) monthLength[1] = 29;

		return day > 0 && day <= monthLength[month - 1];
	};

	const isValidPhone = number => {
		if (!number || number.length !== 12) return false;
		const phoneRx = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/g;
		return number.match(phoneRx);
	};

	const handleInputChange = ({ target: { name, value, required } }) => {
		if (required && name && !value) {
			setErrors({ ...errors, [name]: true });
			return;
		} else {
			const errorsCopy = { ...errors };
			if (errorsCopy[name]) {
				delete errorsCopy[name];
				setErrors(errorsCopy);
			}
		}

		if (name === 'patient_birthdate' && !isValidDate(value)) {
			setErrors({ ...errors, [name]: true });
		}

		if (name === 'clinic_phone' && !isValidPhone(value)) {
			setErrors({ ...errors, [name]: true });
		}

		setFields({ ...fields, [name]: value });
	};

	return (
		<>
			<div
				className={`modal fade ${isDisplayed ? 'show' : ''}`}
				data-backdrop='static'
				id='doctor-contact-sheet'
				tabIndex='-1'
				role='dialog'
				aria-modal={isDisplayed}
				aria-hidden={isDisplayed}
				{...(isDisplayed ? { style: { display: 'block', overflowX: 'hidden', overflowY: 'auto' } } : {})}
			>
				<form className='modal-dialog' role='document'>
					<div className='modal-content'>
						<div className='modal-header border-0'>
							<button type='button' className='close' data-dismiss='modal' aria-label='Close' onClick={onCancel}>
								<svg width='22' height='21' viewBox='0 0 22 21' fill='none' xmlns='http://www.w3.org/2000/svg'>
									<path
										d='M1.625 19.7817L20.375 1.03174'
										stroke='#343A40'
										strokeWidth='1.5'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
									<path
										d='M20.375 19.7817L1.625 1.03174'
										stroke='#343A40'
										strokeWidth='1.5'
										strokeLinecap='round'
										strokeLinejoin='round'
									/>
								</svg>
							</button>
							<h2 className='mt-4'>Have us call your doctor</h2>
							<p className='prescription-description'>
								Enter in your eye doctor’s information, and we’ll reach out to obtain your prescription
								information.
							</p>
						</div>
						<div className='modal-body'>
							<div className='row justify-content-left'>
								<div className='col-md-12'>
									<div className='form-group'>
										<input
											type='text'
											className={`form-control ${errors.patient_full_name ? 'is-invalid' : ''}`}
											name='patient_full_name'
											placeholder="Patient's Full Name"
											required
											onChange={handleInputChange}
										/>
										<small className={`form-text text-danger ${errors.patient_full_name ? '' : 'd-none'}`}>
											Patient's full name is required.
										</small>
									</div>
									<div className='form-group'>
										<InputMask
											type='text'
											mask='99/99/9999'
											className={`form-control ${
												errors.patient_birthdate || errors.invalid_dob ? 'is-invalid' : ''
											}`}
											name='patient_birthdate'
											placeholder="Patient's Birthdate"
											required
											onChange={handleInputChange}
										/>
										{fields.patient_birthdate ? (
											<small
												className={`form-text text-danger ${errors.patient_birthdate ? '' : 'd-none'}`}
											>
												format: mm/dd/yyyy
											</small>
										) : (
											<small
												className={`form-text text-danger ${errors.patient_birthdate ? '' : 'd-none'}`}
											>
												Patient's birthdate is required.
											</small>
										)}
										<div className='why-do-we-ask'>
											<span>Why do we ask for this</span>
											<svg
												width='20'
												height='20'
												viewBox='0 0 20 20'
												fill='none'
												xmlns='http://www.w3.org/2000/svg'
											>
												<g clipPath='url(#clip0)'>
													<path
														d='M11.875 13.75H11.25C10.9185 13.75 10.6005 13.6183 10.3661 13.3839C10.1317 13.1495 10 12.8315 10 12.5V9.375C10 9.20924 9.93415 9.05027 9.81694 8.93306C9.69973 8.81585 9.54076 8.75 9.375 8.75H8.75'
														stroke='#63DFBD'
														strokeWidth='1.5'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M9.6875 5.625C9.62569 5.625 9.56527 5.64333 9.51388 5.67767C9.46249 5.712 9.42244 5.76081 9.39879 5.81791C9.37514 5.87501 9.36895 5.93785 9.381 5.99847C9.39306 6.05908 9.42282 6.11477 9.46653 6.15847C9.51023 6.20217 9.56591 6.23194 9.62653 6.244C9.68715 6.25605 9.74999 6.24986 9.80709 6.22621C9.86419 6.20256 9.913 6.16251 9.94733 6.11112C9.98167 6.05973 10 5.99931 10 5.9375C10 5.85462 9.96708 5.77513 9.90847 5.71653C9.84987 5.65792 9.77038 5.625 9.6875 5.625Z'
														stroke='#63DFBD'
														strokeWidth='1.5'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M10 19.375C15.1777 19.375 19.375 15.1777 19.375 10C19.375 4.82233 15.1777 0.625 10 0.625C4.82233 0.625 0.625 4.82233 0.625 10C0.625 15.1777 4.82233 19.375 10 19.375Z'
														stroke='#63DFBD'
														strokeWidth='1.5'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
												</g>
												<defs>
													<clipPath id='clip0'>
														<rect width='20' height='20' fill='white' />
													</clipPath>
												</defs>
											</svg>
										</div>
									</div>
									<div className='form-group'>
										<input
											type='text'
											className={`form-control ${errors.clinic_name ? 'is-invalid' : ''}`}
											name='clinic_name'
											placeholder='Doctor or Clinic Name'
											required
											onChange={handleInputChange}
										/>
										<small className={`form-text text-danger ${errors.clinic_name ? '' : 'd-none'}`}>
											Doctor or clinic name is required.
										</small>
									</div>
									<div className='form-group'>
										<InputMask
											mask='999-999-9999'
											type='text'
											className={`form-control ${errors.clinic_phone ? 'is-invalid' : ''}`}
											name='clinic_phone'
											placeholder='Doctor or Clinic Phone'
											required
											onChange={handleInputChange}
										/>
										{fields.clinic_phone ? (
											<small className={`form-text text-danger ${errors.clinic_phone ? '' : 'd-none'}`}>
												formate: 000-000-0000
											</small>
										) : (
											<small className={`form-text text-danger ${errors.clinic_phone ? '' : 'd-none'}`}>
												Doctor or clinic phone is required.
											</small>
										)}
									</div>
									<div className='form-group'>
										<select
											className={`custom-select ${errors.clinic_state ? 'is-invalid' : ''}`}
											name='clinic_state'
											placeholder='Doctor or Clinic State/Province'
											required
											onChange={handleInputChange}
										>
											<option value=''>Doctor or Clinic State/Province</option>
											{Object.entries(States.all()).map(([key, state]) => (
												<option key={key} value={key}>
													{state}
												</option>
											))}
										</select>
										<small className={`form-text text-danger ${errors.clinic_state ? '' : 'd-none'}`}>
											State is required.
										</small>
									</div>
								</div>
							</div>
						</div>

						<div className='modal-footer border-0'>
							<div className='half'>
								<a
									className='pair-btn pair-btn__secondary'
									data-dismiss='modal'
									aria-label='Close'
									onClick={onCancel}
								>
									Cancel
								</a>
								<button
									type='button'
									id='submit-call'
									className='pair-btn pair-btn__primary ml-2'
									onClick={handleSubmit}
									disabled={!isEmpty(errors) || !Object.values(fields).every(val => val)}
								>
									Submit<span className='overlay'></span>
								</button>
							</div>
						</div>
					</div>
				</form>
			</div>
			{isDisplayed && <div className='modal-backdrop fade show' />}
		</>
	);
};

export default DoctorModal;
